import React, { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  InputGroup,
  Table,
  Pagination,
  Spinner,
} from "react-bootstrap";
import {
  Download,
  Search,
  RefreshCcw,
  ChevronUp,
  ChevronDown,
} from "react-feather";

import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import { KANNADA_FONT_BASE64 } from "../../../utils/kannadaFontBase64";
import "./ReportPage.css";

import {
  fetchKannadaWasteReportThunk,
  clearKannadaWasteReport,
} from "../../../store/slices/kannadaWasteReportSlice";

// -----------------------------
// Helpers
// -----------------------------
const fmt = (n) => {
  if (n === null || n === undefined || n === "") return "—";
  const x = Number(n);
  if (!Number.isFinite(x)) return String(n);
  return x.toLocaleString("en-IN");
};

const fmtPct = (n) => {
  if (n === null || n === undefined || n === "") return "—";
  const x = Number(n);
  if (!Number.isFinite(x)) return String(n);
  return x.toFixed(2);
};

const formatKannadaFullDate = (dateStr) => {
  if (!dateStr) return "";

  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;

  const months = [
    "ಜನವರಿ",
    "ಫೆಬ್ರವರಿ",
    "ಮಾರ್ಚ್",
    "ಏಪ್ರಿಲ್",
    "ಮೇ",
    "ಜೂನ್",
    "ಜುಲೈ",
    "ಆಗಸ್ಟ್",
    "ಸೆಪ್ಟೆಂಬರ್",
    "ಅಕ್ಟೋಬರ್",
    "ನವೆಂಬರ್",
    "ಡಿಸೆಂಬರ್",
  ];

  const day = String(d.getDate()).padStart(2, "0");
  return `${day} ${months[d.getMonth()]} ${d.getFullYear()}`;
};

const formatSelectedDateRangeKannada = (fromDate, toDate) => {
  if (fromDate && toDate) {
    return `${formatKannadaFullDate(fromDate)} ರಿಂದ ${formatKannadaFullDate(
      toDate
    )}`;
  }
  if (fromDate) return `${formatKannadaFullDate(fromDate)} ರಿಂದ`;
  if (toDate) return `${formatKannadaFullDate(toDate)} ವರೆಗೆ`;
  return "";
};

const withDateLabel = (text, label) => (label ? `${text}\n(${label})` : text);

export default function WasteCoverageReportPage () {
  const dispatch = useDispatch();

  const { rows = [], loading = false, error = null } = useSelector(
    (s) => s.kannadaWasteReport || {}
  );

  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const selectedDateLabel = formatSelectedDateRangeKannada(fromDate, toDate);

  const reportTitle = selectedDateLabel
    ? `ತ್ಯಾಜ್ಯ ನಿರ್ವಹಣೆ ಕವರೇಜ್ ಮಾಹಿತಿ_${selectedDateLabel}`
    : "ತ್ಯಾಜ್ಯ ನಿರ್ವಹಣೆ ಕವರೇಜ್ ಮಾಹಿತಿ";

  const filterLabel = selectedDateLabel || "";

  const safeDateLabel = selectedDateLabel
    ? selectedDateLabel.replace(/\s+/g, "_")
    : "all_dates";

  useEffect(() => {
    setPage(1);
  }, [search, pageSize, rows.length]);

  const handleSort = (field) => {
    setSortField((currentField) => {
      if (currentField === field) {
        setSortDirection((currentDirection) =>
          currentDirection === "asc" ? "desc" : "asc"
        );

        return currentField;
      }

      setSortDirection("asc");
      return field;
    });

    setPage(1);
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return (
        <span className="ms-1 report-sort-default-icon">
          <ChevronUp size={11} />
          <ChevronDown size={11} />
        </span>
      );
    }

    return sortDirection === "asc" ? (
      <ChevronUp size={14} className="ms-1" />
    ) : (
      <ChevronDown size={14} className="ms-1" />
    );
  };

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();

    const dataRows = (rows || []).filter((r) => r.taluk !== "ಒಟ್ಟು");
    const totalRow = (rows || []).find((r) => r.taluk === "ಒಟ್ಟು");

    const searchedRows = !q
      ? [...dataRows]
      : dataRows.filter((r) => {
          const searchableText = [
            r.taluk,
            r.total_houses,
            r.commercial_centers,
            r.total_units,
            r.covered_houses,
            r.covered_commercial,
            r.total_covered,
            r.coverage_pct,
            r.recyclable_waste_kg,
            r.non_recyclable_waste_kg,
            r.mrf_given_kg,
            r.sold_wet_waste_kg,
            r.total_wet_waste_kg,
            r.sanitary_collection_yes_no,
            r.sanitary_estimated_per_day_kg,
          ]
            .map((value) => String(value ?? ""))
            .join(" ")
            .toLowerCase();

          return searchableText.includes(q);
        });

    const numericFields = [
      "total_houses",
      "commercial_centers",
      "total_units",
      "covered_houses",
      "covered_commercial",
      "total_covered",
      "coverage_pct",
      "recyclable_waste_kg",
      "non_recyclable_waste_kg",
      "mrf_given_kg",
      "sold_wet_waste_kg",
      "total_wet_waste_kg",
      "sanitary_estimated_per_day_kg",
    ];

    const sortedRows = !sortField
      ? searchedRows
      : [...searchedRows].sort((a, b) => {
          let aValue = a?.[sortField];
          let bValue = b?.[sortField];

          if (numericFields.includes(sortField)) {
            aValue = Number(aValue || 0);
            bValue = Number(bValue || 0);

            return sortDirection === "asc"
              ? aValue - bValue
              : bValue - aValue;
          }

          const comparison = String(aValue || "").localeCompare(
            String(bValue || ""),
            undefined,
            {
              numeric: true,
              sensitivity: "base",
            }
          );

          return sortDirection === "asc" ? comparison : -comparison;
        });

    return totalRow ? [...sortedRows, totalRow] : sortedRows;
  }, [rows, search, sortField, sortDirection]);

  const total = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);

  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage, pageSize]);

  const handleLoad = () => {
    dispatch(
      fetchKannadaWasteReportThunk({
        from_date: fromDate,
        to_date: toDate,
      })
    );
  };

  const clearAll = () => {
    setSearch("");
    setSortField("");
    setSortDirection("asc");
    setPage(1);
    setFromDate("");
    setToDate("");
    dispatch(clearKannadaWasteReport());
  };

  const exportExcel = () => {
    const data = filteredRows.map((r) => ({
      "ತಾಲೂಕು": r.taluk,
      "ಒಟ್ಟು ಮನೆಗಳು": r.total_houses,
      "ಒಟ್ಟು ವಾಣಿಜ್ಯ ಕೇಂದ್ರಗಳು": r.commercial_centers,
      "ಒಟ್ಟು": r.total_units,
      "ತ್ಯಾಜ್ಯ ಸಂಗ್ರಹಣೆ ಮಾಡಲಾಗುತ್ತಿರುವ ಕುಟುಂಬಗಳು": r.covered_houses,
      "ತ್ಯಾಜ್ಯ ಸಂಗ್ರಹಣೆ ಮಾಡಲಾಗುತ್ತಿರುವ ವಾಣಿಜ್ಯಕೇಂದ್ರಗಳು":
        r.covered_commercial,
      "ಒಟ್ಟು": r.total_covered,
      "%": r.coverage_pct,
      [withDateLabel("ಮಾರಾಟ ಮಾಡಲಾದ ತ್ಯಾಜ್ಯ Recyclable waste (ಕೆ.ಜಿ)", filterLabel)]:
        r.recyclable_waste_kg,
      [
        withDateLabel(
          "ಸಿಮೆಂಟ್ ಫ್ಯಾಕ್ಟರಿಗೆ ವಿಲೇವಾರಿ ಮಾಡಲಾದ ತ್ಯಾಜ್ಯ Non-Recyclable (MLP, Cloths, Sandales etc) (ಕೆ.ಜಿ)",
          filterLabel
        )
      ]: r.non_recyclable_waste_kg,
      [
        withDateLabel(
          "MRF ಯೋಜನೆಗೆ ಒಪ್ಪಿಸಲಾದ / ಗ್ರಾ.ಪಂ ಅಂಗಡಿಗಳಲ್ಲಿ MRF ಗೆ ನೀಡಿದ ತ್ಯಾಜ್ಯ (ಕೆ.ಜಿ)",
          filterLabel
        )
      ]: r.mrf_given_kg,
      [withDateLabel("ಮಾರಾಟ ಮಾಡಲಾದ ಹಸಿ ತ್ಯಾಜ್ಯ (ಕೆ.ಜಿ)", filterLabel)]:
        r.sold_wet_waste_kg,
      [withDateLabel("ಸಂಗ್ರಹಿಸಲಾದ ಒಟ್ಟು ಹಸಿ ತ್ಯಾಜ್ಯ (ಕೆ.ಜಿ)", filterLabel)]:
        r.total_wet_waste_kg,
      "ಸಾನಿಟರಿ ತ್ಯಾಜ್ಯ (Pads, Diapers) ಸಂಗ್ರಹಿಸಲಾಗುತ್ತಿದೆಯೇ?":
        r.sanitary_collection_yes_no,
      "ಹೌದು ಎಂದಾದಲ್ಲಿ ದಿನವೊಂದಕ್ಕೆ ಸಂಗ್ರಹಿಸಲಾಗುವ ಅಂದಾಜು ಪ್ರಮಾಣ (ಕೆ.ಜಿ)":
        r.sanitary_estimated_per_day_kg,
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, `tyajya_report_${safeDateLabel}.xlsx`);
  };

  const exportPDF = () => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a4",
    });

    doc.addFileToVFS("NotoSansKannada-Regular.ttf", KANNADA_FONT_BASE64);
    doc.addFont("NotoSansKannada-Regular.ttf", "NotoSansKannada", "normal");
    doc.setFont("NotoSansKannada", "normal");

    doc.setFontSize(12);
    doc.text(reportTitle, 40, 40);

    const head = [
      [
        "ತಾಲೂಕು",
        "ಒಟ್ಟು ಮನೆಗಳು",
        "ಒಟ್ಟು ವಾಣಿಜ್ಯ ಕೇಂದ್ರಗಳು",
        "ಒಟ್ಟು",
        "ತ್ಯಾಜ್ಯ ಸಂಗ್ರಹಣೆ\nಮಾಡಲಾಗುತ್ತಿರುವ ಕುಟುಂಬಗಳು",
        "ತ್ಯಾಜ್ಯ ಸಂಗ್ರಹಣೆ\nಮಾಡಲಾಗುತ್ತಿರುವ ವಾಣಿಜ್ಯಕೇಂದ್ರಗಳು",
        "ಒಟ್ಟು",
        "%",
        filterLabel
          ? `ಮಾರಾಟ ಮಾಡಲಾದ ತ್ಯಾಜ್ಯ Recyclable waste (ಕೆ.ಜಿ)\n(${filterLabel})`
          : "ಮಾರಾಟ ಮಾಡಲಾದ ತ್ಯಾಜ್ಯ Recyclable waste (ಕೆ.ಜಿ)",
        filterLabel
          ? `ಸಿಮೆಂಟ್ ಫ್ಯಾಕ್ಟರಿಗೆ ವಿಲೇವಾರಿ ಮಾಡಲಾದ ತ್ಯಾಜ್ಯ\nNon-Recyclable (MLP, Cloths, Sandales etc) (ಕೆ.ಜಿ)\n(${filterLabel})`
          : "ಸಿಮೆಂಟ್ ಫ್ಯಾಕ್ಟರಿಗೆ ವಿಲೇವಾರಿ ಮಾಡಲಾದ ತ್ಯಾಜ್ಯ\nNon-Recyclable (MLP, Cloths, Sandales etc) (ಕೆ.ಜಿ)",
        filterLabel
          ? `MRF ಯೋಜನೆಗೆ ಒಪ್ಪಿಸಲಾದ / ಗ್ರಾ.ಪಂ ಅಂಗಡಿಗಳಲ್ಲಿ MRF ಗೆ ನೀಡಿದ ತ್ಯಾಜ್ಯ\n(ಕೆ.ಜಿ)\n(${filterLabel})`
          : "MRF ಯೋಜನೆಗೆ ಒಪ್ಪಿಸಲಾದ / ಗ್ರಾ.ಪಂ ಅಂಗಡಿಗಳಲ್ಲಿ MRF ಗೆ ನೀಡಿದ ತ್ಯಾಜ್ಯ\n(ಕೆ.ಜಿ)",
        filterLabel
          ? `ಮಾರಾಟ ಮಾಡಲಾದ ಹಸಿ ತ್ಯಾಜ್ಯ (ಕೆ.ಜಿ)\n(${filterLabel})`
          : "ಮಾರಾಟ ಮಾಡಲಾದ ಹಸಿ ತ್ಯಾಜ್ಯ (ಕೆ.ಜಿ)",
        filterLabel
          ? `ಸಂಗ್ರಹಿಸಲಾದ ಒಟ್ಟು ಹಸಿ ತ್ಯಾಜ್ಯ (ಕೆ.ಜಿ)\n(${filterLabel})`
          : "ಸಂಗ್ರಹಿಸಲಾದ ಒಟ್ಟು ಹಸಿ ತ್ಯಾಜ್ಯ (ಕೆ.ಜಿ)",
        "ಸಾನಿಟರಿ ತ್ಯಾಜ್ಯ (Pads, Diapers)\nಸಂಗ್ರಹಿಸಲಾಗುತ್ತಿದೆಯೇ?",
        "ಹೌದು ಎಂದಾದಲ್ಲಿ ದಿನವೊಂದಕ್ಕೆ ಸಂಗ್ರಹಿಸಲಾಗುವ\nಅಂದಾಜು ಪ್ರಮಾಣ (ಕೆ.ಜಿ)",
      ],
    ];

    const body = filteredRows.map((r) => [
      r.taluk,
      fmt(r.total_houses),
      fmt(r.commercial_centers),
      fmt(r.total_units),
      fmt(r.covered_houses),
      fmt(r.covered_commercial),
      fmt(r.total_covered),
      fmtPct(r.coverage_pct),
      fmt(r.recyclable_waste_kg),
      fmt(r.non_recyclable_waste_kg),
      fmt(r.mrf_given_kg),
      fmt(r.sold_wet_waste_kg),
      fmt(r.total_wet_waste_kg),
      r.sanitary_collection_yes_no || "—",
      fmt(r.sanitary_estimated_per_day_kg),
    ]);

    autoTable(doc, {
      startY: 60,
      head,
      body,
      theme: "grid",
      styles: {
        font: "NotoSansKannada",
        fontStyle: "normal",
        fontSize: 8,
        cellPadding: 3,
        overflow: "linebreak",
        valign: "middle",
      },
      headStyles: {
        font: "NotoSansKannada",
        fontStyle: "normal",
        fontSize: 7,
        halign: "center",
        valign: "middle",
      },
      bodyStyles: {
        font: "NotoSansKannada",
        fontStyle: "normal",
      },
      didParseCell: (data) => {
        const rowTaluk = data.row?.raw?.[0];
        if (rowTaluk === "ಒಟ್ಟು") data.cell.styles.fontStyle = "bold";
      },
      margin: { left: 20, right: 20 },
      showHead: "everyPage",
    });

    doc.save(`tyajya_report_${safeDateLabel}.pdf`);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const maxButtons = 5;
    let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let end = Math.min(totalPages, start + maxButtons - 1);
    start = Math.max(1, end - maxButtons + 1);

    items.push(
      <Pagination.Prev
        key="prev"
        disabled={currentPage === 1}
        onClick={() => setPage((p) => Math.max(1, p - 1))}
      />
    );

    for (let p = start; p <= end; p++) {
      items.push(
        <Pagination.Item
          key={p}
          active={p === currentPage}
          onClick={() => setPage(p)}
        >
          {p}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        disabled={currentPage === totalPages}
        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
      />
    );

    return <Pagination className="mb-0">{items}</Pagination>;
  };

  return (
    <div>
      <div className="report-page">
        <Card className="report-card">
          <Card.Header className="report-card-header">
            <div className="report-header-row">
              <div>
                <div className="report-title">{reportTitle}</div>
              </div>
            </div>
          </Card.Header>

          <Card.Body>
            {error ? <div className="alert alert-danger mb-3">{error}</div> : null}

            <Row className="g-3 align-items-end mb-3">
              <Col lg={3} md={6}>
                <Form.Label className="report-label">From Date</Form.Label>
                <Form.Control
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </Col>

              <Col lg={3} md={6}>
                <Form.Label className="report-label">To Date</Form.Label>
                <Form.Control
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </Col>

              <Col lg={6} md={12} className="d-flex gap-2">
                <Button
                  className="report-btn"
                  variant="primary"
                  onClick={handleLoad}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Load
                    </>
                  ) : (
                    "Load"
                  )}
                </Button>

                <Button
                  className="report-btn"
                  variant="outline-secondary"
                  onClick={clearAll}
                >
                  <RefreshCcw size={16} className="me-2" />
                  Clear
                </Button>
              </Col>
            </Row>

            <Row className="g-3 align-items-end mb-3">
              <Col lg={6} md={8}>
                <Form.Label className="report-label">Search</Form.Label>
                <InputGroup className="report-search">
                  <InputGroup.Text>
                    <Search size={16} />
                  </InputGroup.Text>
                  <Form.Control
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="ತಾಲೂಕು / ಸಂಖ್ಯೆ..."
                  />
                </InputGroup>
              </Col>

              <Col lg={6} md={4} className="d-flex justify-content-end gap-2">
                <Button
                  className="report-btn"
                  variant="outline-primary"
                  onClick={exportExcel}
                  disabled={!filteredRows.length}
                >
                  <Download size={16} className="me-2" />
                  Excel
                </Button>

                <Button
                  className="report-btn"
                  variant="outline-danger"
                  onClick={exportPDF}
                  disabled={!filteredRows.length}
                >
                  <Download size={16} className="me-2" />
                  PDF
                </Button>
              </Col>
            </Row>

            <div className="report-table-wrap">
              <div className="report-table-scroll">
                <Table hover className="mb-0 report-table">
                  <thead>
                    <tr>
                      <th style={{ minWidth: 120 }}>
                        <button
                          type="button"
                          className={`report-sort-btn ${
                            sortField === "taluk" ? "is-active" : ""
                          }`}
                          onClick={() => handleSort("taluk")}
                        >
                          ತಾಲೂಕು
                          {renderSortIcon("taluk")}
                        </button>
                      </th>

                      <th className="text-end">
                        <button
                          type="button"
                          className={`report-sort-btn ${
                            sortField === "total_houses" ? "is-active" : ""
                          }`}
                          onClick={() => handleSort("total_houses")}
                        >
                          ಒಟ್ಟು ಮನೆಗಳು
                          {renderSortIcon("total_houses")}
                        </button>
                      </th>

                      <th className="text-end">
                        <button
                          type="button"
                          className={`report-sort-btn ${
                            sortField === "commercial_centers" ? "is-active" : ""
                          }`}
                          onClick={() => handleSort("commercial_centers")}
                        >
                          ಒಟ್ಟು ವಾಣಿಜ್ಯ ಕೇಂದ್ರಗಳು
                          {renderSortIcon("commercial_centers")}
                        </button>
                      </th>

                      <th className="text-end">
                        <button
                          type="button"
                          className={`report-sort-btn ${
                            sortField === "total_units" ? "is-active" : ""
                          }`}
                          onClick={() => handleSort("total_units")}
                        >
                          ಒಟ್ಟು
                          {renderSortIcon("total_units")}
                        </button>
                      </th>

                      <th className="text-end">
                        <button
                          type="button"
                          className={`report-sort-btn ${
                            sortField === "covered_houses" ? "is-active" : ""
                          }`}
                          onClick={() => handleSort("covered_houses")}
                        >
                          ತ್ಯಾಜ್ಯ ಸಂಗ್ರಹಣೆ ಮಾಡಲಾಗುತ್ತಿರುವ ಕುಟುಂಬಗಳು
                          {renderSortIcon("covered_houses")}
                        </button>
                      </th>

                      <th className="text-end">
                        <button
                          type="button"
                          className={`report-sort-btn ${
                            sortField === "covered_commercial" ? "is-active" : ""
                          }`}
                          onClick={() => handleSort("covered_commercial")}
                        >
                          ತ್ಯಾಜ್ಯ ಸಂಗ್ರಹಣೆ ಮಾಡಲಾಗುತ್ತಿರುವ ವಾಣಿಜ್ಯಕೇಂದ್ರಗಳು
                          {renderSortIcon("covered_commercial")}
                        </button>
                      </th>

                      <th className="text-end">
                        <button
                          type="button"
                          className={`report-sort-btn ${
                            sortField === "total_covered" ? "is-active" : ""
                          }`}
                          onClick={() => handleSort("total_covered")}
                        >
                          ಒಟ್ಟು
                          {renderSortIcon("total_covered")}
                        </button>
                      </th>

                      <th className="text-end">
                        <button
                          type="button"
                          className={`report-sort-btn ${
                            sortField === "coverage_pct" ? "is-active" : ""
                          }`}
                          onClick={() => handleSort("coverage_pct")}
                        >
                          %
                          {renderSortIcon("coverage_pct")}
                        </button>
                      </th>

                      <th className="text-end">
                        <button
                          type="button"
                          className={`report-sort-btn ${
                            sortField === "recyclable_waste_kg" ? "is-active" : ""
                          }`}
                          onClick={() => handleSort("recyclable_waste_kg")}
                        >
                          <span>
                            ಮಾರಾಟ ಮಾಡಲಾದ ತ್ಯಾಜ್ಯ Recyclable waste (ಕೆ.ಜಿ)
                            {filterLabel ? (
                              <>
                                <br />({filterLabel})
                              </>
                            ) : null}
                          </span>
                          {renderSortIcon("recyclable_waste_kg")}
                        </button>
                      </th>

                      <th className="text-end">
                        <button
                          type="button"
                          className={`report-sort-btn ${
                            sortField === "non_recyclable_waste_kg" ? "is-active" : ""
                          }`}
                          onClick={() => handleSort("non_recyclable_waste_kg")}
                        >
                          <span>
                            ಸಿಮೆಂಟ್ ಫ್ಯಾಕ್ಟರಿಗೆ ವಿಲೇವಾರಿ ಮಾಡಲಾದ ತ್ಯಾಜ್ಯ
                            <br />
                            Non-Recyclable (MLP, Cloths, Sandales etc) (ಕೆ.ಜಿ)
                            {filterLabel ? (
                              <>
                                <br />({filterLabel})
                              </>
                            ) : null}
                          </span>
                          {renderSortIcon("non_recyclable_waste_kg")}
                        </button>
                      </th>

                      <th className="text-end">
                        <button
                          type="button"
                          className={`report-sort-btn ${
                            sortField === "mrf_given_kg" ? "is-active" : ""
                          }`}
                          onClick={() => handleSort("mrf_given_kg")}
                        >
                          <span>
                            MRF ಯೋಜನೆಗೆ ಒಪ್ಪಿಸಲಾದ / ಗ್ರಾ.ಪಂ ಅಂಗಡಿಗಳಲ್ಲಿ MRF ಗೆ
                            ನೀಡಿದ ತ್ಯಾಜ್ಯ
                            <br />
                            (ಕೆ.ಜಿ)
                            {filterLabel ? (
                              <>
                                <br />({filterLabel})
                              </>
                            ) : null}
                          </span>
                          {renderSortIcon("mrf_given_kg")}
                        </button>
                      </th>

                      <th className="text-end">
                        <button
                          type="button"
                          className={`report-sort-btn ${
                            sortField === "sold_wet_waste_kg" ? "is-active" : ""
                          }`}
                          onClick={() => handleSort("sold_wet_waste_kg")}
                        >
                          <span>
                            ಮಾರಾಟ ಮಾಡಲಾದ ಹಸಿ ತ್ಯಾಜ್ಯ (ಕೆ.ಜಿ)
                            {filterLabel ? (
                              <>
                                <br />({filterLabel})
                              </>
                            ) : null}
                          </span>
                          {renderSortIcon("sold_wet_waste_kg")}
                        </button>
                      </th>

                      <th className="text-end">
                        <button
                          type="button"
                          className={`report-sort-btn ${
                            sortField === "total_wet_waste_kg" ? "is-active" : ""
                          }`}
                          onClick={() => handleSort("total_wet_waste_kg")}
                        >
                          <span>
                            ಸಂಗ್ರಹಿಸಲಾದ ಒಟ್ಟು ಹಸಿ ತ್ಯಾಜ್ಯ (ಕೆ.ಜಿ)
                            {filterLabel ? (
                              <>
                                <br />({filterLabel})
                              </>
                            ) : null}
                          </span>
                          {renderSortIcon("total_wet_waste_kg")}
                        </button>
                      </th>

                      <th className="text-end">
                        <button
                          type="button"
                          className={`report-sort-btn ${
                            sortField === "sanitary_collection_yes_no" ? "is-active" : ""
                          }`}
                          onClick={() => handleSort("sanitary_collection_yes_no")}
                        >
                          <span>
                            ಸಾನಿಟರಿ ತ್ಯಾಜ್ಯ (Pads, Diapers)
                            <br />
                            ಸಂಗ್ರಹಿಸಲಾಗುತ್ತಿದೆಯೇ?
                          </span>
                          {renderSortIcon("sanitary_collection_yes_no")}
                        </button>
                      </th>

                      <th className="text-end">
                        <button
                          type="button"
                          className={`report-sort-btn ${
                            sortField === "sanitary_estimated_per_day_kg" ? "is-active" : ""
                          }`}
                          onClick={() => handleSort("sanitary_estimated_per_day_kg")}
                        >
                          <span>
                            ಹೌದು ಎಂದಾದಲ್ಲಿ ದಿನವೊಂದಕ್ಕೆ ಸಂಗ್ರಹಿಸಲಾಗುವ
                            <br />
                            ಅಂದಾಜು ಪ್ರಮಾಣ (ಕೆ.ಜಿ)
                          </span>
                          {renderSortIcon("sanitary_estimated_per_day_kg")}
                        </button>
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={15} className="text-center py-4">
                          <Spinner animation="border" size="sm" className="me-2" />
                          Loading...
                        </td>
                      </tr>
                    ) : pagedRows.length ? (
                      pagedRows.map((r, i) => {
                        const isTotal = r.taluk === "ಒಟ್ಟು";

                        return (
                          <tr
                            key={`${r.taluk}-${i}`}
                            className={isTotal ? "fw-bold" : ""}
                          >
                            <td>
                              <b>{r.taluk}</b>
                            </td>
                            <td className="text-end">{fmt(r.total_houses)}</td>
                            <td className="text-end">{fmt(r.commercial_centers)}</td>
                            <td className="text-end">{fmt(r.total_units)}</td>
                            <td className="text-end">{fmt(r.covered_houses)}</td>
                            <td className="text-end">{fmt(r.covered_commercial)}</td>
                            <td className="text-end">{fmt(r.total_covered)}</td>
                            <td className="text-end">{fmtPct(r.coverage_pct)}</td>
                            <td className="text-end">{fmt(r.recyclable_waste_kg)}</td>
                            <td className="text-end">{fmt(r.non_recyclable_waste_kg)}</td>
                            <td className="text-end">{fmt(r.mrf_given_kg)}</td>
                            <td className="text-end">{fmt(r.sold_wet_waste_kg)}</td>
                            <td className="text-end">{fmt(r.total_wet_waste_kg)}</td>
                            <td className="text-end">
                              {r.sanitary_collection_yes_no || "—"}
                            </td>
                            <td className="text-end">
                              {fmt(r.sanitary_estimated_per_day_kg)}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={15} className="text-center py-4">
                          No data found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              <div className="report-pagination-bar">
                <div className="report-page-size">
                  <span>Rows:</span>
                  <Form.Select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                    className="page-size-select"
                  >
                    {[10, 20, 50, 100].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </Form.Select>
                </div>

                <div className="report-pagination">{renderPagination()}</div>

                <div className="report-page-info">
                  Page {currentPage} / {totalPages} • Total {total}
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
}